import { Component, NgZone, OnInit, PLATFORM_ID, inject, signal } from '@angular/core';
import { AuthenticatorService, AmplifyAuthenticatorModule } from '@aws-amplify/ui-angular';
import { AmplifyService } from './services/amplify.service';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { UtilitiesService } from './services/utilities.service';
import { Router, RouterLinkWithHref, RouterOutlet } from '@angular/router';
import { HeaderComponent } from './components/header/header.component';
import { ConfirmComponent } from './components/confirm/confirm.component';
import { MessageComponent } from './components/message/message.component';
import { signIn } from 'aws-amplify/auth';
import { PermissionsService } from './services/permissions.service';
import { catchError, of, take } from 'rxjs';
import { ButtonComponent } from './components/button/button.component';
import { FooterComponent } from './components/footer/footer.component';
import { RoundButtonComponent } from './components/round-button/round-button.component';
import MemoryAdapter from './services/webauthn/memory-adapter.service';
import { CredentialsService } from './services/webauthn/credentials.service';
import { LoggingService } from './services/logging.service';
import { NotificationComponent } from './components/notification/notification.component';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  standalone: true,
  imports: [
    CommonModule,
    RouterOutlet,
    RouterLinkWithHref,
    NotificationComponent,
    ConfirmComponent,
    AmplifyAuthenticatorModule,
    HeaderComponent,
    ButtonComponent,
    FooterComponent,
    RoundButtonComponent,
  ],
})
export class AppComponent implements OnInit {
  public async ngOnInit(): Promise<void> {
    if (isPlatformBrowser(this.platformId)) {
      this.utilitiesService.setLocale(window?.location.pathname.split('/')[1]);
    }

    await this.memoryAdapter.createKey();
    const user = this.memoryAdapter.getValue('user');
    if (this.memoryAdapter.getValue(`pwd-${user}`)) {
      this.credentialsService.activateBiometric();
    }
    this.biometricEnable.update(() => !!this.memoryAdapter.getValue('biometric'));

    this.authenticatorService.subscribe((authenticator) => {
      this.amplifyService.signedIn(authenticator.authStatus === 'authenticated');
      this.showLanding.update(() => authenticator.authStatus !== 'authenticated');

      this.isSignedIn.set(authenticator.authStatus === 'authenticated');
      if (this.isSignedIn()) {
        this.amplifyService.loggedInUser(authenticator.user);
      }

      this.showLogin.update(() => !this.isSignedIn());
    });
  }

  public services(that: AppComponent): { handleSignIn(formData: Record<string, any>): Promise<any> } {
    return {
      async handleSignIn(formData: Record<string, any>): Promise<any> {
        let { username, password } = formData;
        username = username.toLocaleLowerCase().trim();
        password = password.trim();

        const user = await signIn({
          username,
          password,
        });

        that.biometricEnable.update(() => true);
        that.credentialsService.setUserCredential({ username, password });
        that.permissionsService
          .loadPermissions()
          .pipe(
            catchError((error) => {
              that.logger.error({ message: $localize`Error loading permissions`, error });
              return of([]);
            }),
          )
          .subscribe();
        that.utilitiesService.setCurrentRoute('summary_ui');
        that.router.navigate(['/summary']);

        return user;
      },
    };
  }

  public async biometricLogin(): Promise<void> {
    const authAbortController = new AbortController();
    authAbortController.abort();

    const user = this.memoryAdapter.getValue('user') ?? '';
    const userCred = await this.memoryAdapter.getCred();

    this.credentialsService
      .loginUser(user)
      .pipe(
        take(1),
        catchError((error) => {
          this.logger.error({ message: $localize`Fetch user error`, error });
          return of({ status: '' });
        }),
      )
      .subscribe((response) => {
        if (response.status === 'ok') {
          this.amplifyService.signedIn(true);
          signIn({ username: userCred?.username ?? '', password: userCred?.password ?? '' }).then(() => {
            this.ngZone.run(() => {
              if (navigator.onLine) {
                window.location.reload();
              }
            });
          });
        }
      });
  }

  public showLoginComponent(): void {
    this.showLogin.update(() => true);
  }

  private readonly platformId = inject(PLATFORM_ID);
  private readonly authenticatorService = inject(AuthenticatorService);
  private readonly amplifyService = inject(AmplifyService);
  private readonly utilitiesService = inject(UtilitiesService);
  private readonly permissionsService = inject(PermissionsService);
  private readonly router = inject(Router);
  private readonly memoryAdapter = inject(MemoryAdapter);
  private readonly credentialsService = inject(CredentialsService);
  private readonly logger = inject(LoggingService);
  private readonly ngZone = inject(NgZone);

  public biometricActivated = this.credentialsService.biometricActivated;
  public isSignedIn = signal<boolean>(false);
  public showLanding = signal<boolean>(true);
  public showLogin = signal<boolean>(false);
  public biometricEnable = signal<boolean>(false);
  public locale = this.utilitiesService.locale;
  public formFields = {
    signIn: {
      username: {
        labelHidden: false,
        placeholder: $localize`Enter your email`,
        isRequired: true,
      },
      password: {
        labelHidden: false,
        placeholder: $localize`Enter your password`,
        isRequired: true,
      },
    },
  };
}
